/**
 * QUIPWA — Deck-level average retention.
 *
 * The `deck_stats` VIEW hardcodes `avg_retention` to 0 because SQLite has no
 * POW(), so the real figure has to be derived in TypeScript from the deck's
 * review_state rows. Kept pure and separate from StateDBService so it can be
 * tested directly, and so every caller agrees on the same number.
 */

import { retrievability } from './fsrs-algorithm.ts';

export interface RetentionRow {
  /** FSRS stability, in days. Rows with stability <= 0 are not retained yet. */
  stability: number;
  /** Unix seconds of the last review, or null if never reviewed. */
  last_review: number | null;
}

/**
 * Mean current retrievability across `rows`.
 *
 * Returns null — not 0 — when there is nothing to average. A deck nobody has
 * studied has no retention, which reads very differently from a deck the user
 * has forgotten completely; the UI renders the two differently too.
 *
 * @param nowTs evaluation time in unix seconds (injected so this stays pure)
 */
export function averageRetention(rows: RetentionRow[], nowTs: number): number | null {
  let sum = 0;
  let count = 0;

  for (const row of rows) {
    if (!(row.stability > 0)) continue;
    const elapsedDays = row.last_review
      ? Math.max(0, (nowTs - row.last_review) / 86400)
      : 0;
    const r = retrievability(elapsedDays, row.stability);
    sum += Math.min(1, Math.max(0, r));
    count++;
  }

  return count > 0 ? sum / count : null;
}

/** Group rows by deck, then average each group. Decks with no qualifying rows
 *  are absent from the map (rather than mapped to 0). */
export function averageRetentionByDeck(
  rows: Array<RetentionRow & { deck_instance_id: string }>,
  nowTs: number
): Map<string, number> {
  const grouped = new Map<string, RetentionRow[]>();
  for (const row of rows) {
    const bucket = grouped.get(row.deck_instance_id);
    if (bucket) bucket.push(row);
    else grouped.set(row.deck_instance_id, [row]);
  }

  const out = new Map<string, number>();
  for (const [deckId, deckRows] of grouped) {
    const avg = averageRetention(deckRows, nowTs);
    if (avg !== null) out.set(deckId, avg);
  }
  return out;
}
